import { SaveOperation } from '@infuse/api/common';
import { CreateOrganisation } from '@infuse/api/iam/organisation';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericCreateOrganisation = ({
  apiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<CreateOrganisation>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, { parentId?: string; organisation: CreateOrganisation }>({
    mutationFn: async ({ parentId, organisation }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        await apiFn(parentId ? { parentId: parentId } : {}, organisation);
        notify({
          title: t('iam.organisations.header.create_organisation'),
          message: t('iam.organisations.notify.create_organisation_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.organisations.header.create_organisation'),
          message: getAxiosErrorMessage(e, t('iam.organisations.notify.create_organisation_failed'))
        });
        throw e;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organisations'] });
    }
  });

  return {
    ...mutation
  };
};
